// ChannelPushForm.jsx — push notification payload editor with char counters + EN/AR tabs
import React, { useState } from 'react';
import { FormGroup, Input, Label, Nav, NavItem, NavLink } from 'reactstrap';
import ImageUploader from 'react-images-upload';
import { storage } from '../../../../../helpers/Firebase';
import DeepLinkPicker from './DeepLinkPicker.jsx';

const PUSH_TITLE_MAX = 65;
const PUSH_BODY_MAX = 240;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB — FCM rich-push cap is 1 MB but we upload full-fidelity and let FCM handle downscaling

// `context` — 'campaign' | 'automation'. Passed through to DeepLinkPicker so
// the destination dropdown hides per-user routes (booking, rate) from campaigns.
const ChannelPushForm = ({ value, onChange, context = 'automation' }) => {
  const [lang, setLang] = useState('en');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const v = value || { localized: { en: {}, ar: {} }, deepLink: { route: 'home', params: {} } };
  const cur = v.localized?.[lang] || {};

  const setLocalized = (lang, patch) => onChange({
    ...v,
    localized: { ...v.localized, [lang]: { ...(v.localized?.[lang] || {}), ...patch } },
  });
  const setDeepLink = (dl) => onChange({ ...v, deepLink: dl });

  const charCount = (s, max) => {
    const n = (s || '').length;
    const color = n > max ? '#dc3545' : (n > max * 0.85 ? '#f0ad4e' : '#8f8f8f');
    return <span style={{ fontSize: '0.75rem', color }}>{n}/{max}</span>;
  };

  // Uploads dropped image to Firebase Storage under marketing/push_images/, sets imageUrl
  // per-language on the campaign/automation payload. Empty pictures[] = user cleared the image.
  const onImageDrop = async (pictures) => {
    setUploadError(null);
    if (!pictures || pictures.length === 0) {
      setLocalized(lang, { imageUrl: null });
      return;
    }
    const file = pictures[pictures.length - 1];
    if (!(file instanceof File) && !(file instanceof Blob)) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setUploadError(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`);
      return;
    }
    setUploading(true);
    try {
      const ext = (file.name || 'image.png').split('.').pop() || 'png';
      const path = `marketing/push_images/${lang}_${Date.now()}.${ext}`;
      const ref = storage().ref().child(path);
      const uploadTask = ref.put(file, { cacheControl: 'public,max-age=300', contentType: file.type || 'image/png' });
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed', null, reject, resolve);
      });
      const url = await uploadTask.snapshot.ref.getDownloadURL();
      setLocalized(lang, { imageUrl: url });
    } catch (e) {
      console.error('image upload failed', e);
      setUploadError(e.message || 'Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            active={lang === 'en'}
            onClick={() => setLang('en')}
            style={{ cursor: 'pointer' }}
          >EN</NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={lang === 'ar'}
            onClick={() => setLang('ar')}
            style={{ cursor: 'pointer' }}
          >AR</NavLink>
        </NavItem>
      </Nav>

      <FormGroup>
        <div className="d-flex justify-content-between align-items-baseline">
          <Label className="mb-0">Title</Label>
          {charCount(cur.title, PUSH_TITLE_MAX)}
        </div>
        <Input
          value={cur.title || ''}
          onChange={(e) => setLocalized(lang, { title: e.target.value })}
          placeholder={lang === 'ar' ? 'العنوان' : 'Headline (kept short — first line shown in lock screen)'}
          maxLength={PUSH_TITLE_MAX}
        />
      </FormGroup>

      <FormGroup>
        <div className="d-flex justify-content-between align-items-baseline">
          <Label className="mb-0">Body</Label>
          {charCount(cur.body, PUSH_BODY_MAX)}
        </div>
        <Input
          type="textarea"
          rows={3}
          value={cur.body || ''}
          onChange={(e) => setLocalized(lang, { body: e.target.value })}
          placeholder={lang === 'ar' ? 'نص الرسالة' : 'Message body'}
          maxLength={PUSH_BODY_MAX}
        />
      </FormGroup>

      <FormGroup>
        <Label>Image <span className="text-muted small">(optional — drag and drop, or click to browse)</span></Label>
        <ImageUploader
          key={`push-image-${lang}-${cur.imageUrl || 'none'}`}
          className="w-100"
          withIcon={true}
          label="Max 5 MB — jpg, png, gif"
          buttonText="Choose image"
          onChange={onImageDrop}
          imgExtension={['.jpg', '.jpeg', '.gif', '.png']}
          maxFileSize={MAX_IMAGE_BYTES}
          withPreview={true}
          singleImage={true}
          defaultImages={cur.imageUrl ? [cur.imageUrl] : []}
        />
        {uploading && (
          <div className="text-muted small mt-1">Uploading…</div>
        )}
        {uploadError && <div className="text-danger small mt-1">{uploadError}</div>}
      </FormGroup>

      <hr />

      <DeepLinkPicker value={v.deepLink} onChange={setDeepLink} context={context} />
    </div>
  );
};

export default ChannelPushForm;
